import { MetricoolOrganicPanel } from '../components/MetricoolOrganicPanel';
import { CampaignPanel } from '../components/CampaignPanel';
import React, { useState, useEffect, useCallback } from 'react';
import { api, useAuth } from '../App';
import MetricCard from '../components/MetricCard';
import DashboardChart from '../components/DashboardChart';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Separator } from '../components/ui/separator';
import { Calendar as CalendarIcon, FileText, Download, Loader2, RefreshCw, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { format, subDays, startOfMonth, endOfMonth, subMonths, startOfQuarter } from 'date-fns';

const DATE_PRESETS = [
  { label: 'Last 7 days', getDates: () => ({ start: subDays(new Date(), 7), end: new Date() }) },
  { label: 'Last 30 days', getDates: () => ({ start: subDays(new Date(), 30), end: new Date() }) },
  { label: 'This Month', getDates: () => ({ start: startOfMonth(new Date()), end: new Date() }) },
  { label: 'Last Month', getDates: () => ({ start: startOfMonth(subMonths(new Date(), 1)), end: endOfMonth(subMonths(new Date(), 1)) }) },
];

const PLATFORM_LABEL = { facebook: 'Facebook', instagram: 'Instagram', linkedin: 'LinkedIn', tiktok: 'TikTok', youtube: 'YouTube' };
function fmt(d) { return format(d, 'yyyy-MM-dd'); }

export default function ClientPortalPage({ navigate }) {
  const { user } = useAuth();
  const clientId = user?.client_id;
  const [client, setClient] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState({ start: subDays(new Date(), 30), end: subDays(new Date(), 1) });
  const [calendarOpen, setCalendarOpen] = useState(false);

  const fetchData = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const [c, m, r] = await Promise.all([
        api(`/api/clients/${clientId}`),
        api(`/api/clients/${clientId}/metrics?start_date=${fmt(dateRange.start)}&end_date=${fmt(dateRange.end)}`),
        api(`/api/reports?client_id=${clientId}`),
      ]);
      setClient(c);
      setMetrics(m);
      setReports(r);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [clientId, dateRange]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const org = metrics?.organic_summary || {};
  const paid = metrics?.paid_summary || {};
  const platforms = metrics?.platforms || {};
  const enabledPlatforms = client?.platforms || [];
  const dl = metrics?.date_labels || [];

  if (loading && !metrics) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin" size={24} /></div>;
  }

  return (
    <div className="animate-fade-in-up">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            {client?.name || 'Dashboard'}
          </h1>
          <p className="text-xs text-muted-foreground">
            {fmt(dateRange.start)} to {fmt(dateRange.end)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm">
                <CalendarIcon size={14} className="mr-2" />
                {format(dateRange.start, 'MMM d')} – {format(dateRange.end, 'MMM d, yyyy')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-4" align="end">
              <div className="space-y-3">
                <div className="flex flex-wrap gap-1.5">
                  {DATE_PRESETS.map(p => (
                    <Button key={p.label} variant="outline" size="sm" className="text-xs" onClick={() => { setDateRange(p.getDates()); setCalendarOpen(false); }}>
                      {p.label}
                    </Button>
                  ))}
                </div>
                <Separator />
                <Calendar
                  mode="range"
                  selected={{ from: dateRange.start, to: dateRange.end }}
                  onSelect={(range) => {
                    if (range?.from) setDateRange({ start: range.from, end: range.to || range.from });
                    if (range?.from && range?.to && range.from.getTime() !== range.to.getTime()) setCalendarOpen(false);
                  }}
                  numberOfMonths={1}
                  defaultMonth={dateRange.start}
                />
              </div>
            </PopoverContent>
          </Popover>
          <Button variant="outline" size="icon" onClick={fetchData} className="h-8 w-8">
            <RefreshCw size={14} />
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6 h-auto flex flex-wrap justify-start gap-1 [&>button]:min-h-11" data-testid="client-portal-tabs">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          {enabledPlatforms.map(p => (
            <TabsTrigger key={p} value={p}>{PLATFORM_LABEL[p] || p}</TabsTrigger>
          ))}
          {Object.keys(paid).length > 0 && <TabsTrigger value="paid">Paid Ads</TabsTrigger>}
          <TabsTrigger value="reports">Reports</TabsTrigger>
        </TabsList>

        {/* Overview */}
        <TabsContent value="overview">
          {metrics?.data_source==='metricool'?<MetricoolOrganicPanel data={metrics.metricool} internal={false}/>:<div className="space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <MetricCard label="Impressions" value={org.impressions?.current || 0} changePct={org.impressions?.change_pct || 0} />
              <MetricCard label="Engagements" value={org.engagements?.current || 0} changePct={org.engagements?.change_pct || 0} />
              <MetricCard label="Engagement Rate" value={org.engagement_rate?.current || 0} suffix="%" changePct={org.engagement_rate?.change_pct || 0} />
              <MetricCard label="Follower Growth" value={org.follower_growth?.current || 0} prefix="+" changePct={org.follower_growth?.change_pct || 0} />
            </div>
            {dl.length > 0 && (
              <DashboardChart
                title="Impressions by Platform"
                chartType="area"
                height={300}
                data={Object.fromEntries(enabledPlatforms.filter(p => platforms[p]?.daily_impressions || platforms[p]?.daily_views).map(p => [p, platforms[p]?.daily_impressions || platforms[p]?.daily_views]))}
                dataKeys={enabledPlatforms.filter(p => platforms[p]?.daily_impressions || platforms[p]?.daily_views).map(p => ({ key: p, name: PLATFORM_LABEL[p] || p }))}
                dateLabels={dl}
              />
            )}
          </div>}
        </TabsContent>

        {/* Platform tabs */}
        {enabledPlatforms.map(pkey => {
          const p = platforms[pkey] || {};
          return (
            <TabsContent key={pkey} value={pkey}>
              {metrics?.data_source==='metricool'?<MetricoolOrganicPanel data={metrics.metricool} platform={pkey} internal={false}/>:<div className="space-y-6">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  {p.impressions && <MetricCard label="Impressions" value={p.impressions.current} changePct={p.impressions.change_pct} />}
                  {p.engagements && <MetricCard label="Engagements" value={p.engagements.current} changePct={p.engagements.change_pct} />}
                  {p.engagement_rate && <MetricCard label="ER" value={p.engagement_rate.current} suffix="%" changePct={p.engagement_rate.change_pct} />}
                  {p.followers && <MetricCard label="Followers" value={p.followers.current} changePct={p.followers.change_pct} />}
                </div>
                {p.date_labels && (p.daily_impressions || p.daily_views) && (
                  <DashboardChart
                    title={`${PLATFORM_LABEL[pkey] || pkey} Performance`}
                    chartType="area"
                    data={{
                      ...(p.daily_impressions ? { imp: p.daily_impressions } : {}),
                      ...(p.daily_views ? { views: p.daily_views } : {}),
                      ...(p.daily_engagements ? { eng: p.daily_engagements } : {}),
                    }}
                    dataKeys={[
                      ...(p.daily_impressions ? [{ key: 'imp', name: 'Impressions' }] : []),
                      ...(p.daily_views ? [{ key: 'views', name: 'Views' }] : []),
                      ...(p.daily_engagements ? [{ key: 'eng', name: 'Engagements' }] : []),
                    ]}
                    dateLabels={p.date_labels}
                  />
                )}
              </div>}
            </TabsContent>
          );
        })}

        {/* Paid */}
        {Object.keys(paid).length > 0 && (
          <TabsContent value="paid">
            <CampaignPanel clientId={clientId} startDate={fmt(dateRange.start)} endDate={fmt(dateRange.end)}/>
          </TabsContent>
        )}

        {/* Reports */}
        <TabsContent value="reports">
          <div className="space-y-4">
            {reports.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <FileText size={32} className="mx-auto text-muted-foreground/30 mb-3" />
                  <p className="text-sm text-muted-foreground">No reports available yet</p>
                </CardContent>
              </Card>
            ) : (
              reports.map(report => (
                <Card key={report._id} className="metric-card-hover">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white" style={{ background: '#1f2a4d' }}>
                          <FileText size={18} />
                        </div>
                        <div>
                          <h3 className="font-semibold">{report.client_name} Report</h3>
                          <div className="text-xs text-muted-foreground mt-0.5">
                            {report.date_range?.start} to {report.date_range?.end} · {new Date(report.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => navigate('report-builder', { reportId: report._id, clientId: report.client_id })}>
                          <Eye size={14} className="mr-1" /> View
                        </Button>
                        <Button variant="outline" size="sm" onClick={async () => {
                          const token = localStorage.getItem('token');
                          const res = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/reports/${report._id}/pdf`, { headers: { Authorization: `Bearer ${token}` }});
                          const blob = await res.blob();
                          const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
                          const a = document.createElement('a'); a.href = url; a.download = `${report.client_name}_report.pdf`; document.body.appendChild(a); a.click(); document.body.removeChild(a);
                        }}>
                          <Download size={14} className="mr-1" /> PDF
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
